/** Lawnmower grid in the rotated frame where flight lines are horizontal (spec §4.1 steps 4–7). */
import { dist } from './frame';
import { bounds, pointInPolygon, rectTouches } from './polygon';
import type { MissionInputs, XY } from './types';

/** Seconds allowed for each turn between lines in the flight-time estimate. */
export const TURN_PENALTY_S = 3;

export interface GridLine {
  y: number;
  /** Photo centres along the line, ascending; the first and last lie beyond the area. */
  xs: number[];
}

/**
 * Evenly spaced positions centred on [lo, hi]: the fewest such that the outermost one on each side
 * lies outside the extent, i.e. floor(extent / spacing) + 2 (spec §4.1 steps 4–5).
 */
function straddle(lo: number, hi: number, spacing: number): number[] {
  const count = Math.floor((hi - lo) / spacing + 1e-9) + 2;
  const start = (lo + hi) / 2 - ((count - 1) * spacing) / 2;
  return Array.from({ length: count }, (_, k) => start + k * spacing);
}

export function gridLines(polyRot: XY[], lineSpacing: number, photoSpacing: number): GridLine[] {
  const b = bounds(polyRot);
  // A regular lattice: photo columns shared by every line, so photos line up across lines.
  const ys = straddle(b.minY, b.maxY, lineSpacing);
  const xs = straddle(b.minX, b.maxX, photoSpacing);
  // A point is photographed when it lies in the area, or when it is next to one that does in any
  // of the eight directions (so the outer lines keep their corners). An area too small to hold any
  // lattice point falls back to the points whose cell (half a spacing around them) touches it.
  let inside = ys.map((y) => xs.map((x) => pointInPolygon(polyRot, { x, y })));
  if (!inside.some((row) => row.some(Boolean))) {
    inside = ys.map((y) => xs.map((x) => rectTouches(polyRot, { x0: x - photoSpacing / 2, x1: x + photoSpacing / 2, y0: y - lineSpacing / 2, y1: y + lineSpacing / 2 })));
  }
  const keep = (k: number, j: number) => {
    for (let dk = -1; dk <= 1; dk++) for (let dj = -1; dj <= 1; dj++) if (inside[k + dk]?.[j + dj]) return true;
    return false;
  };
  return ys.map((y, k) => ({ y, xs: xs.filter((_, j) => keep(k, j)) })).filter((l) => l.xs.length > 0);
}

function traverse(lines: GridLine[], startTop: boolean, firstReversed: boolean): GridLine[] {
  const seq = startTop ? [...lines].reverse() : [...lines];
  return seq.map((l, i) => {
    const reversed = (i % 2 === 1) !== firstReversed;
    return { y: l.y, xs: reversed ? [...l.xs].reverse() : [...l.xs] };
  });
}

/** Boustrophedon order; with a takeoff point, the one of four traversals that minimises takeoff→first + last→takeoff; ties go to the shorter takeoff→first leg. */
export function orderLines(lines: GridLine[], takeoffRot: XY | null): GridLine[] {
  if (lines.length === 0) return [];
  if (!takeoffRot) return traverse(lines, false, false);
  let best: GridLine[] | null = null;
  let bestCost = Infinity;
  let bestFirst = Infinity;
  for (const startTop of [false, true]) {
    for (const firstReversed of [false, true]) {
      const t = traverse(lines, startTop, firstReversed);
      const first = { x: t[0].xs[0], y: t[0].y };
      const lastLine = t[t.length - 1];
      const last = { x: lastLine.xs[lastLine.xs.length - 1], y: lastLine.y };
      const firstLeg = dist(takeoffRot, first);
      const cost = firstLeg + dist(last, takeoffRot);
      if (cost < bestCost - 1e-9 || (Math.abs(cost - bestCost) <= 1e-9 && firstLeg < bestFirst - 1e-9)) {
        best = t;
        bestCost = cost;
        bestFirst = firstLeg;
      }
    }
  }
  return best ?? traverse(lines, false, false);
}

export function deriveSpeed(inputs: MissionInputs, photoSpacing: number, maxSpeed: number): { speed_ms: number; capped: boolean } {
  if (inputs.trigger === 'waypoint') return { speed_ms: Math.min(inputs.speed_ms, maxSpeed), capped: false };
  const raw = photoSpacing / inputs.interval_s;
  return raw > maxSpeed ? { speed_ms: maxSpeed, capped: true } : { speed_ms: raw, capped: false };
}
